import { DescriptionOutlined } from '@mui/icons-material';
import { formatDistance } from 'date-fns';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AgreementsTable,
    Alert,
    AlertButton,
    Child,
    Container,
    HomeHeading,
    InProgressAgreementsTable,
    Spinner,
    StyledButton,
    Subtitle,
    Title,
} from '../components';
import { getIncompleteAgreements, useIdentity, useUserAgreements } from '../services';
import { AuthService } from '../services/AuthService';
import type { IIncompleteAggrement } from '../types';
import { AdminDashboard, fullTableHeaders, partialTableHeaders } from './homepage';

export const Home = () => {
    const { identity } = useIdentity();
	const isAdmin = identity.data.roleId === 2;
	const name = `${identity.data.firstName || ''} ${identity.data.lastName || ''}`;

	return (
		<>
            {identity.isComplete ? (
				isAdmin ? (
					<AdminDashboard />
				) : (
					<Dashboard name={`Welcome ${name}`} identity={identity} />
				)
			) : (
				<Spinner />
			)}
		</>
	);
};

const Dashboard = ({ name, identity }: { name: string; identity: { data: IIncompleteAggrement; isComplete: boolean; isFailure: boolean } }) => {
	const navigate = useNavigate();
	const { userAgreements } = useUserAgreements();
	const [incompleteAgreements, setIncompleteAgreements] = useState<IIncompleteAggrement[]>([]);

	useEffect(() => {
        if (identity.isFailure) {
			AuthService.logout();
			navigate('/login');
			return;
		}

        if (identity.isComplete) {
			const result = async () =>
				getIncompleteAgreements(identity.data.id)
					.then(fleetCompany => {
						setIncompleteAgreements(fleetCompany.data.items);
					});
			result();
		}
    }, [identity.data.id, identity.isComplete, identity.isFailure, navigate]);

	const isEmpty =
		userAgreements.data.expiring.data.length <= 0 &&
		userAgreements.data.current.data.length <= 0 &&
		userAgreements.data.past.data.length <= 0;

	return (
		<Container container>
            <HomeHeading container>
				<Title>{name}</Title>
				<StyledButton onClick={() => navigate('/agreement/new')}>CREATE NEW AGREEMENT</StyledButton>
			</HomeHeading>
			{userAgreements.data.expiring.data.length > 0 && (
                <Child>
					{userAgreements.data.expiring.data.map((alert: any, idx: number) => (
						<Alert
							message={`NOTE: Your agreement with Allison Transmission expires in ${formatDistance(
								new Date(),
								new Date(alert.expirationDate)
							)}. To continue services, please create a new agreement.`}
							action={
								<AlertButton
									label={`NEW AGREEMENT FOR ${alert.tspProvider.toUpperCase()}`}
									icon={<DescriptionOutlined style={{ marginRight: '4px' }} />}
									onClick={() => navigate('/agreement/new')}
								/>
							}
							key={idx}
						/>
					))}
				</Child>
			)}
			{incompleteAgreements.length > 0 && (
                <Child>
					<Subtitle component="h2" style={{ textAlign: 'left', marginTop: '16px' }}>
						Incomplete Agreements - Missing Fields
					</Subtitle>
					<InProgressAgreementsTable headers={partialTableHeaders} data={incompleteAgreements} />
				</Child>
			)}
			{userAgreements.data.current.data.length > 0 && (
                <Child>
					<Subtitle component="h2" style={{ textAlign: 'left' }}>
						{userAgreements.data.current.title}
					</Subtitle>
					<AgreementsTable headers={fullTableHeaders} data={userAgreements.data.current.data} />
				</Child>
			)}
			{userAgreements.data.past.data.length > 0 && (
                <Child>
					<Subtitle component="h2" style={{ textAlign: 'left' }}>
						{userAgreements.data.past.title}
					</Subtitle>
					<AgreementsTable headers={fullTableHeaders} data={userAgreements.data.past.data} />
				</Child>
			)}
			{isEmpty && (
                <Child style={{ marginTop: '50px' }}>
					<Title>To sign a data sharing agreement, click the "Create New Agreement" button above.</Title>
				</Child>
			)}
		</Container>
	);
};
